import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { EScreenLockEntityType, IScreenLockTarget, SCREEN_LOCK_MAX_TOKENS_PER_REQUEST } from './screen-lock.interface';

interface IHeldToken {
  expiresAt: number;
  token: string;
}

/**
 * Holds the API-minted unlock tokens, keyed by screen. Memory only, never persist — losing them on reload is
 * the point (persisting would let a reload carry the unlock without the PIN). See SCREEN_LOCK_CONTEXT.md.
 */
@Injectable({ providedIn: 'root' })
export class ScreenLockTokenService {
  private readonly heldTokens = new Map<string, IHeldToken>();
  private readonly unlockedSubject = new Subject<IScreenLockTarget>();

  /** Emits whenever a screen is unlocked, so a request that was refused can be retried once it is. */
  public readonly unlocked$: Observable<IScreenLockTarget> = this.unlockedSubject.asObservable();

  private static buildKey({ entityType, entityId }: IScreenLockTarget): string {
    return `${entityType}:${entityId}`;
  }

  public store(target: IScreenLockTarget, token: string, expiresInSeconds: number): void {
    this.heldTokens.set(ScreenLockTokenService.buildKey(target), {
      expiresAt: Date.now() + expiresInSeconds * 1000,
      token,
    });

    this.unlockedSubject.next(target);
  }

  /**
   * Live tokens, comma separated, for the request header. Prunes expired entries as it goes.
   *
   * Newest first, capped at what the API reads. The API takes the first
   * {@link SCREEN_LOCK_MAX_TOKENS_PER_REQUEST} tokens and drops the rest silently, so sending them in Map
   * insertion order handed it the *oldest* ones — past ten held unlocks the token just minted never arrived
   * and the write stayed refused with no way out. All tokens share one TTL, so a later expiry means a later
   * unlock. See SCREEN_LOCK_CONTEXT.md.
   */
  public buildHeaderValue(): string | null {
    const now: number = Date.now();
    const liveTokens: IHeldToken[] = [];

    this.heldTokens.forEach((held: IHeldToken, key: string) => {
      if (held.expiresAt <= now) {
        this.heldTokens.delete(key);

        return;
      }

      liveTokens.push(held);
    });

    if (!liveTokens.length) {
      return null;
    }

    return liveTokens
      .sort((first: IHeldToken, second: IHeldToken) => second.expiresAt - first.expiresAt)
      .slice(0, SCREEN_LOCK_MAX_TOKENS_PER_REQUEST)
      .map((held: IHeldToken) => held.token)
      .join(',');
  }

  public has(target: IScreenLockTarget): boolean {
    const held: IHeldToken | undefined = this.heldTokens.get(ScreenLockTokenService.buildKey(target));

    return !!held && held.expiresAt > Date.now();
  }

  /** Called when a screen re-locks: the unlock it was granted must not outlive it. */
  public clear(target: IScreenLockTarget): void {
    this.heldTokens.delete(ScreenLockTokenService.buildKey(target));
  }

  public clearAll(): void {
    this.heldTokens.clear();
  }

  /** Convenience for call sites that only have the two halves of a target. */
  public clearEntity(entityType: EScreenLockEntityType, entityId: number): void {
    this.clear({ entityId, entityType });
  }
}
